import { Routes } from '@angular/router';
import { redirectUnauthorizedTo } from './guards/auth.guard';
import { requirePermission, requireOrg } from './guards/permission.guard';

export const routes: Routes = [
  {
    // Neutral interstitial during global sign-out (no guard: shown while the
    // session is being torn down). See SigningOutComponent.
    path: 'signing-out',
    loadComponent: () =>
      import('./pages/signing-out/signing-out.component').then((m) => m.SigningOutComponent),
  },
  {
    path: '__/auth',
    loadComponent: () =>
      import('./pages/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent,
      ),
  },
  {
    path: 'auth-redirect',
    loadComponent: () =>
      import('./pages/auth-redirect/auth-redirect.component').then(
        (m) => m.AuthRedirectComponent,
      ),
  },
  {
    // Data residency: the active org's region can't be reached (requireOrg).
    path: 'region-unavailable',
    loadComponent: () =>
      import('./pages/region-unavailable/region-unavailable.component').then(
        (m) => m.RegionUnavailableComponent,
      ),
    canActivate: [redirectUnauthorizedTo('/login')],
  },
  {
    // Login is centralized in the auth portal. This route only needs to resolve
    // so code that navigates to `/login` (e.g. sign-out) hands off to the portal:
    // the guard calls bridge.login() for unauthenticated users before the (empty)
    // component renders.
    path: 'login',
    loadComponent: () =>
      import('./pages/auth-handoff/auth-handoff.component').then((m) => m.AuthHandoffComponent),
    canActivate: [redirectUnauthorizedTo('/login')],
  },
  {
    // Full-screen module editor — chat + code + live preview, no app sidebar.
    path: 'modules/:id',
    loadComponent: () =>
      import('./pages/module-editor/module-editor.component').then((m) => m.ModuleEditorComponent),
    canActivate: [redirectUnauthorizedTo('/login'), requireOrg()],
  },
  {
    // Full-screen newsletter builder — chat + block canvas + live preview, no app sidebar.
    path: 'newsletters/:id',
    loadComponent: () =>
      import('./pages/newsletters/newsletter-workspace.component').then((m) => m.NewsletterWorkspaceComponent),
    canActivate: [redirectUnauthorizedTo('/login'), requireOrg()],
  },
  {
    // Full-screen flow builder — chat + automation node graph, no app sidebar.
    path: 'flows/:id',
    loadComponent: () =>
      import('./pages/flows/flow-workspace.component').then((m) => m.FlowWorkspaceComponent),
    canActivate: [redirectUnauthorizedTo('/login'), requireOrg()],
  },
  {
    path: '',
    loadComponent: () => import('./layout/layout.component').then((m) => m.LayoutComponent),
    canActivate: [redirectUnauthorizedTo('/login')],
    children: [
      {
        path: 'new',
        loadComponent: () => import('./pages/new-user/new-user.component').then((m) => m.NewUserComponent),
      },
      {
        path: 'home',
        loadComponent: () => import('./pages/home/home.component').then((m) => m.HomeComponent),
        canActivate: [requireOrg()],
      },
      {
        path: 'modules',
        loadComponent: () =>
          import('./pages/modules/modules.component').then((m) => m.ModulesComponent),
        canActivate: [requireOrg()],
      },
      {
        path: 'brandings',
        loadComponent: () =>
          import('./pages/brandings/brandings.component').then((m) => m.BrandingsComponent),
        canActivate: [requireOrg()],
      },
      {
        path: 'brandings/:id',
        loadComponent: () =>
          import('./pages/brandings/branding-editor.component').then((m) => m.BrandingEditorComponent),
        canActivate: [requireOrg()],
      },
      {
        path: 'assets',
        loadComponent: () => import('./pages/assets/assets.component').then((m) => m.AssetsComponent),
        canActivate: [requireOrg()],
      },
      {
        path: 'newsletters',
        loadComponent: () =>
          import('./pages/newsletters/newsletters.component').then((m) => m.NewslettersComponent),
        canActivate: [requireOrg()],
      },
      {
        path: 'flows',
        loadComponent: () =>
          import('./pages/flows/flows.component').then((m) => m.FlowsComponent),
        canActivate: [requireOrg()],
      },
      {
        path: 'organizations',
        loadComponent: () =>
          import('./pages/organizations/organizations.component').then((m) => m.OrganizationsComponent),
        canActivate: [requireOrg(), requirePermission('members.read')],
      },
      {
        path: 'account',
        loadComponent: () => import('./pages/account/account.component').then((m) => m.AccountComponent),
      },
      {
        path: 'settings',
        redirectTo: 'account',
      },
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
    ],
  },
];
